import Tooltip from '@mui/material/Tooltip';
import { Box } from '@mui/system';
import { ActionButton } from '@openx/components-header/HeaderActions/ActionButton';
import { getInitialPckValues } from '@openx/components-package/components/PackageForm/constants';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { type DealFormData, type DealFormDataWithTargeting, dealFormField, pmpDealType } from '@openx/types/deal';
import { useIsDemandPartnerSelected } from '@openx/utils/hooks/useIsDemandPartnerSelected';
import { DV360_DEMAND_PARTNER_ID } from '@openx/utils/services/config';
import isEqual from 'lodash/isEqual';
import type { JSX } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../../utils/context/DealContext';
import { useUpdateForecastState } from '../hooks/useUpdateForecastState';

export const GenerateForecastButton = (): JSX.Element | null => {
  const { useGetForecastJobId, useDealForecast, isOa } = useDealContext();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { t } = useTranslation();
  const {
    forecastState: { package_loading, loading: foreacastLoading, disabled: forecastDisabled },
    actions: { updateDealParticipants },
  } = useDealForecast();

  const { getFieldState } = useFormContext();

  const observableDealParticipants = useWatch<DealFormDataWithTargeting, typeof dealFormField.DEAL_PARTICIPANTS>({
    name: dealFormField.DEAL_PARTICIPANTS,
  }) as DealFormData['deal_participants'];
  const observablePmpDealType = useWatch<DealFormDataWithTargeting, typeof dealFormField.PMP_DEAL_TYPE>({
    name: dealFormField.PMP_DEAL_TYPE,
  });
  const observablePackage = useWatch<DealFormDataWithTargeting, typeof dealFormField.PACKAGE_UID>({
    name: dealFormField.PACKAGE_UID,
  });
  const observableTargeting = useWatch<DealFormDataWithTargeting, 'targeting.targeting'>({
    name: 'targeting.targeting',
  });

  const shouldSelectExistingPckg = getFieldState(dealFormField.PACKAGE_UID).invalid;

  const isDemandPartner = useIsDemandPartnerSelected(observableDealParticipants);
  const { postForecast, isLoading } = useGetForecastJobId(DV360_DEMAND_PARTNER_ID);

  const isForecastWithTargetingEnabled = isBefInstance && observableTargeting;
  const isPackageAvailable = isForecastWithTargetingEnabled ? false : !observablePackage;
  const isWithinOpenAuction = observablePmpDealType === pmpDealType.WithinOpenAuction;
  const forecastButtonDisabled =
    !isDemandPartner ||
    !isWithinOpenAuction ||
    isPackageAvailable ||
    isLoading ||
    package_loading ||
    foreacastLoading ||
    shouldSelectExistingPckg ||
    isEqual(getInitialPckValues().targeting, observableTargeting);

  const observableItems = {
    observableDealParticipants,
    observablePackage,
    observablePmpDealType,
    observableTargeting,
  };

  useUpdateForecastState({ disableButton: !!forecastButtonDisabled, observableFormItems: observableItems });

  const tooltipWording = isOa
    ? t('In order to generate a Deal Plan, you must select a DSP and a set of targeting.')
    : t(
        'In order to generate a Deal Plan, you must select a DSP, a set of targeting and your deal must be Within Open Auction.'
      );

  return (
    <Tooltip
      title={!isDemandPartner || !isWithinOpenAuction || !observablePackage ? tooltipWording : ''}
      placement="top-start"
    >
      <Box component={'span'}>
        <ActionButton
          color="default"
          data-test="generate-forecast-button"
          label={t('Generate Plan')}
          disabled={forecastDisabled}
          onClick={async () => {
            try {
              await postForecast();
              updateDealParticipants(observableDealParticipants);
            } catch (error) {
              // biome-ignore lint/suspicious/noConsole: logging error in console
              console.error(error);
            }
          }}
        />
      </Box>
    </Tooltip>
  );
};
